import { useMemo, useSyncExternalStore } from 'react';
import { DEFAULT_SETTINGS } from '@core/settings/schema.ts';
import type { SettingsStore } from '@ports/SettingsStore.ts';
import { getUiLocale, subscribeUiLocale } from '../i18n/index.ts';
import { deriveStatusResponse, deriveUnlock, membershipStatusLabel } from '../lib/membership.ts';

export type MembershipState = {
  unlock: boolean;
  /** Raw status.response from statusJson (OK | Session Expired | Not Found | …). */
  statusResponse: string;
  statusLabel: string;
  userId: string;
  userToken: string;
};

export function useMembership(settingsStore: SettingsStore): MembershipState {
  const settings = useSyncExternalStore(
    (cb) => settingsStore.subscribe(cb),
    () => settingsStore.getSnapshot?.() ?? DEFAULT_SETTINGS,
  );
  const locale = useSyncExternalStore(subscribeUiLocale, getUiLocale, () => 'en' as const);

  return useMemo(() => {
    const statusJson = settings.statusJson ?? '';
    const unlock = deriveUnlock(statusJson);
    return {
      unlock,
      statusResponse: deriveStatusResponse(statusJson),
      statusLabel: membershipStatusLabel(statusJson, unlock),
      userId: settings.userId ?? '',
      userToken: settings.userToken ?? '',
    };
  }, [settings, locale]);
}
